import { cloneDeep, toInteger } from 'lodash';
import {
    createCanvas,
    createForeignObjectImageUrl,
    fetchVectorText,
    loadImageAsync,
    loadSvgAsync,
    serializerDomToString,
} from '@/utils/file-kit';
import { getLayerFreshDOM } from '@/utils/layer-kit';
import { transferXYToDistance } from '@/utils/math-kit';
import { VectorType, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { SCALE_HEIGHT_RATIO, SCALE_RATIO, SCALE_WIDTH_RATIO } from '../constants';
import { renderDom } from '../views/render-dom';
import { checkIsUsingEffect } from './effect';
import { getTextContentDOMSelector } from './text';

type Options = {
    isPPT: boolean;
};

export class LayerCanvasCreator {
    private readonly layer: ILayerProps;
    private readonly layerData: IVectorLayerProps;

    private readonly enableOpacity: boolean;
    private readonly isUsingEffect: boolean;

    private drawLayer: ILayerProps | null = null; // 用于绘制带特效的图层数据

    private effectLayoutInfluence = {
        scaleWidth: 0,
        scaleHeight: 0,
        offsetY: 0,
        reflectionHeight: 0,
    };

    constructor(
        private identity: string,
        layer: ILayerProps,
        enableOpacity = true,
    ) {
        const layerData = layer.data as IVectorLayerProps;

        this.layer = layer;
        this.layerData = layerData;

        this.enableOpacity = enableOpacity;
        this.isUsingEffect = checkIsUsingEffect(layerData, {
            includeBlur: true,
        });
    }

    public getEffectLayoutInfluence() {
        return this.effectLayoutInfluence;
    }

    private async loadSVGContent() {
        const { url } = this.layerData;
        if (url) return fetchVectorText(url);
    }

    private resizeLayerByEffect(options: Options) {
        const isPPTMode = options.isPPT;

        const { layer } = this;
        this.drawLayer = cloneDeep(this.layer);

        const effect = this.layerData.effect;
        const shadow = effect.shadow[0];
        const reflection = effect.reflection[0];

        let shadowOffsetX = 0;
        if (shadow) {
            shadowOffsetX = transferXYToDistance(shadow.x, shadow.y);
        }

        let reflectionOffsetY = 0;
        let reflectionHeight = 0;
        if (reflection) {
            reflectionOffsetY = reflection.y;
            reflectionHeight = reflection.size ? (reflection.size / 100) * layer.height : 0;
        }

        const totalWidth = layer.width;
        let totalHeight = layer.height;

        /**
         * 倒影的三种情况：
         * 1. 倒影完全在图层下方
         * 2. 倒影和图层有重叠
         * 3. 倒影完全在图层上方
         *
         * 对高度计算的影响：
         * 情况 1、2：即 reflectionOffsetY 大于等于 -reflectionHeight， totalHeight = 图层高度 + 偏移距离 + 倒影大小
         * 情况 3：即 reflectionOffsetY 小于 -layer.height， totalHeight = 偏移距离（此时偏移距离为图层底部到倒影顶部）
         */

        let offsetY = 0;

        if (reflectionOffsetY >= -reflectionHeight) {
            totalHeight = layer.height + reflectionHeight + reflectionOffsetY;
        } else if (reflectionOffsetY < -layer.height) {
            totalHeight = Math.abs(reflectionOffsetY);
            offsetY = totalHeight - layer.height;
        }

        const widthRatio = !isPPTMode ? SCALE_RATIO : SCALE_WIDTH_RATIO;
        const heightRatio = !isPPTMode ? SCALE_RATIO : SCALE_HEIGHT_RATIO;

        const finalWidth = Math.ceil((totalWidth + shadowOffsetX) * widthRatio);
        const finalHeight = Math.ceil((totalHeight + shadowOffsetX) * heightRatio);

        const widthDelta = Math.floor((finalWidth - totalWidth) / 2);
        const heightDelta = Math.floor((finalHeight - totalHeight) / 2);

        const nextLeft = widthDelta;
        const nextTop = heightDelta + offsetY;

        this.drawLayer.left = nextLeft;
        this.drawLayer.top = nextTop;
        this.drawLayer.rotate = 0;

        this.effectLayoutInfluence = {
            scaleWidth: widthDelta,
            scaleHeight: heightDelta,
            offsetY,
            reflectionHeight: totalHeight - layer.height,
        };

        return {
            width: finalWidth,
            height: finalHeight,
        };
    }

    private async createCanvasWithEffect(options: Options) {
        const { width, height } = this.resizeLayerByEffect(options);
        const drawLayer = this.drawLayer as ILayerProps;

        const dispose = await renderDom(this.identity, drawLayer);

        const selector = `[data-layer-id^="${this.layer.id}"]`; // 这里需要用前缀匹配，因为系统可能对图层id加上某些后缀

        const element = getLayerFreshDOM(selector);

        if (!element) {
            dispose?.();
            return null;
        }

        // 图片置于中心
        const cloned = element.cloneNode(true) as HTMLElement;
        const translateX = (width - drawLayer.width) / 2;
        const translateY = (() => {
            const baseDelta = (height - drawLayer.height) / 2;

            // case: 倒影完全在图层上方
            if (this.effectLayoutInfluence.offsetY) {
                return baseDelta + this.effectLayoutInfluence.offsetY / 2;
            }

            return baseDelta - this.effectLayoutInfluence.reflectionHeight / 2;
        })();

        cloned.style.transformOrigin = '50% 50%';
        cloned.style.transform = `translate(${translateX}px, ${translateY}px) rotate(0deg)`;

        // 需要将文本内容去除，文本另外使用文本图层的出图能力
        const textContentSelector = getTextContentDOMSelector(this.layer.id);
        const textContentDOM = cloned.querySelector(textContentSelector);
        if (textContentDOM) textContentDOM.remove();

        // 去除 reactive-area (图层宽度缩小到最小时，图层内会多出 reactive-area)
        const reactiveAreaDOM = cloned.querySelector('.reactive-area');
        if (reactiveAreaDOM) reactiveAreaDOM.remove();

        const svgString = createForeignObjectImageUrl(width, height, serializerDomToString(cloned));
        const imageSource = await loadSvgAsync(svgString);

        dispose?.();

        const { canvas, ctx } = createCanvas(width, height);
        ctx.drawImage(imageSource, 0, 0);

        return canvas;
    }

    private async createNormalCanvas() {
        const selector = `[data-draw-id="svg-${this.layer.id}"]`;

        const dispose = await renderDom(this.identity, this.layer);
        let imageSource = getLayerFreshDOM(selector) as HTMLImageElement | null;

        if (!imageSource) {
            dispose?.();
            return null;
        }

        const { width, height } = this.layer;
        const { vectorType } = this.layerData;

        if (vectorType === VectorType.Ellipse) {
            const svgString = createForeignObjectImageUrl(width, height, serializerDomToString(imageSource));
            imageSource = await loadSvgAsync(svgString);
        } else {
            imageSource = await loadImageAsync(imageSource.src);
        }

        const { imageTransform } = this.layerData;
        const {
            flipH,
            flipV,
            scale,
            rotate: imgRotate,
            widthRatio,
            heightRatio,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
        } = imageTransform;

        const resultWidth = toInteger(width);
        const resultHeight = toInteger(height);
        const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
        const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
        const originImgWidth = toInteger(width * widthRatio);
        const originImgHeight = toInteger(height * heightRatio);

        let imgOffsetX = resultWidth / 2;
        let imgOffsetY = resultHeight / 2;

        if (flipH) {
            imgOffsetX = imgOffsetX - offsetX;
        } else {
            imgOffsetX = imgOffsetX + offsetX;
        }

        if (flipV) {
            imgOffsetY = imgOffsetY - offsetY;
        } else {
            imgOffsetY = imgOffsetY + offsetY;
        }

        // --- 创建图层 canvas ---
        const { canvas: layerCanvas, ctx: layerCtx } = createCanvas(resultWidth, resultHeight);
        layerCtx.translate(imgOffsetX, imgOffsetY);

        const rotate = flipH || flipV ? -imgRotate : imgRotate;
        layerCtx.rotate((rotate * Math.PI) / 180);
        layerCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
        layerCtx.drawImage(imageSource, -originImgWidth / 2, -originImgHeight / 2, originImgWidth, originImgHeight);

        // 根据是否应用透明度进行绘制
        if (!this.enableOpacity) {
            dispose?.();
            return layerCanvas;
        }

        const { opacity } = this.layer;
        const { canvas: finalCanvas, ctx: finalCtx } = createCanvas(resultWidth, resultHeight);
        finalCtx.globalAlpha = opacity;
        finalCtx.drawImage(layerCanvas, 0, 0, resultWidth, resultHeight);
        dispose?.();
        return finalCanvas;
    }

    public async create(options: Options) {
        try {
            await this.loadSVGContent();
        } catch (error) {
            console.log('load svg error', error);
            return createCanvas(1, 1).canvas;
        }

        if (this.isUsingEffect) {
            return this.createCanvasWithEffect(options);
        }

        return this.createNormalCanvas();
    }
}
